import { Inject, Injectable } from '@angular/core'
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr'
import { HttpErrorResponse } from '@angular/common/http'
import { compact, get } from 'lodash'
import { of } from 'rxjs'
import { delay, take, tap } from 'rxjs/operators'
import { ENVIRONMENT } from '../tokens'
import { BackendTranslationService } from './backend-translation.service'

declare const window: any

export interface Toast extends ActiveToast<any> {}

@Injectable()
export class Toaster {
  constructor(
    private toastr: ToastrService,
    @Inject(ENVIRONMENT) private environment,
    private backendTranslation: BackendTranslationService
  ) {}

  success(message?: string, title = $localize`Success`, options?: Partial<IndividualConfig>): Toast {
    return this.toastr.success(message, title, this.options(options))
  }

  info(message?: string, title = $localize`Info`, options?: Partial<IndividualConfig>): Toast {
    return this.toastr.info(message, title, this.options(options))
  }

  warning(message?: string, title = $localize`Warning`, options?: Partial<IndividualConfig>): void {
    of(true)
      .pipe(
        delay(10),
        tap(() => this.toastr.warning(message, title, this.options(options))),
        take(1)
      )
      .subscribe()
  }

  error(message?: string, title = $localize`Error`, error?: any, options?: Partial<IndividualConfig>): Toast {
    if (error) {
      // console.log('[toaster] error:', error);

      const errorMessage = this.getError(error)

      if (errorMessage && !title) {
        title = message
        message = errorMessage
      } else {
        message = compact([message, this.getError(error)]).join('<br>')
      }
    }

    return this.toastr.error(message, title, this.options(options))
  }

  clear() {
    return this.toastr.clear()
  }

  private options(opts: Partial<IndividualConfig>): Partial<IndividualConfig> {
    return {
      closeButton: true,
      enableHtml: true,
      onActivateTick: true,
      ...opts,
    }
  }

  private getErrorMessage(error: string | object) {
    if (!error) {
      return ''
    }

    if (error instanceof HttpErrorResponse) {
      return this.getErrorMessage(get(error, 'error'))
    } else if (Array.isArray(error)) {
      // is array of strings
      return error.map((t) => this.getErrorMessage(t)).join('. ')
    } else if (typeof error === 'object') {
      return this.getErrorMessage(get(error, 'message')) || this.getErrorMessage(get(error, 'error'))
    }

    return error
  }

  getError(error: any): string {
    if (error instanceof HttpErrorResponse) {
      const response: HttpErrorResponse = error as HttpErrorResponse

      if (!response.status && !window.navigator.onLine) {
        return $localize`Please check your internet connection.`
      } else if (response.url && response.url.startsWith(this.environment.apiUrl)) {
        const rawMessage = this.getErrorMessage(response)
        return this.backendTranslation.translate(rawMessage)
      }
    }

    const rawMessage = this.getErrorMessage(error)
    return this.backendTranslation.translate(rawMessage)
  }
}
